/**
 * Deferred secondary product-card images: idle prefetch + load-gated hover reveal.
 * Uses delegated pointer/focus events so hover works for every card, including
 * cards injected later (show-all / AJAX grid reloads) without requiring a rebind.
 */

/** @param {HTMLImageElement} img @param {HTMLElement} media */
function markSecondaryReady(img, media) {
  img.classList.add('is-loaded');
  media.classList.add('specimen-card__media--has-secondary');
}

/**
 * @param {HTMLElement} media
 * @param {Document} [doc]
 * @returns {HTMLImageElement|null}
 */
export function loadCardSecondary(media, doc = typeof document !== 'undefined' ? document : undefined) {
  if (!media || !doc) return null;
  if (media.dataset.secondaryLoaded === 'true') {
    return media.querySelector('.specimen-card__image--secondary');
  }

  const src = media.dataset.secondarySrc;
  if (!src) return null;

  media.dataset.secondaryLoaded = 'true';

  const img = doc.createElement('img');
  img.className = 'specimen-card__image specimen-card__image--secondary';
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  img.decoding = 'async';
  img.loading = 'eager';
  if (media.dataset.secondarySrcset) img.srcset = media.dataset.secondarySrcset;
  if (media.dataset.secondarySizes) img.sizes = media.dataset.secondarySizes;
  img.src = src;

  const onReady = () => markSecondaryReady(img, media);
  if (img.complete && img.naturalWidth > 0) onReady();
  else {
    img.addEventListener('load', onReady, { once: true });
    img.addEventListener('error', onReady, { once: true });
  }

  media.appendChild(img);
  return img;
}

/**
 * Warm HTTP cache for a card secondary without showing it.
 * @param {HTMLElement} media
 */
export function prefetchCardSecondary(media) {
  if (!media || media.dataset.secondaryPrefetched === 'true') return;
  const src = media.dataset.secondarySrc;
  if (!src) return;

  media.dataset.secondaryPrefetched = 'true';
  const probe = new Image();
  if (media.dataset.secondarySrcset) probe.srcset = media.dataset.secondarySrcset;
  if (media.dataset.secondarySizes) probe.sizes = media.dataset.secondarySizes;
  probe.decoding = 'async';
  probe.src = src;
}

/**
 * @param {ParentNode} [root]
 * @returns {IntersectionObserver | null}
 */
export function initSecondaryPrefetch(root = typeof document !== 'undefined' ? document : undefined) {
  if (!root || typeof IntersectionObserver === 'undefined') return null;

  const schedule = (fn) => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 150);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        schedule(() => prefetchCardSecondary(/** @type {HTMLElement} */ (entry.target)));
      });
    },
    { rootMargin: '120px 0px' },
  );

  root.querySelectorAll('[data-secondary-src]').forEach((media) => {
    if (media.dataset.secondaryLoaded !== 'true' && media.dataset.secondaryPrefetchObserved !== 'true') {
      media.dataset.secondaryPrefetchObserved = 'true';
      observer.observe(media);
    }
  });

  return observer;
}

/**
 * Resolve deferred media from an event target (card or media node).
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
export function mediaFromEventTarget(target) {
  if (!target || typeof target !== 'object' || !('closest' in target)) return null;
  const el = /** @type {Element} */ (target);
  const media = el.closest?.('[data-secondary-src]');
  if (media) return /** @type {HTMLElement} */ (media);
  const card = el.closest?.('.specimen-card');
  return card ? /** @type {HTMLElement | null} */ (card.querySelector('[data-secondary-src]')) : null;
}

/**
 * Bind hover/focus activation for deferred secondary images.
 * Safe to call repeatedly; uses document-level delegation once.
 * @param {ParentNode} [root] - Optional subtree to (re)observe for idle prefetch
 */
export function bindCardHover(root = typeof document !== 'undefined' ? document : undefined) {
  if (!root) return;

  if (typeof document !== 'undefined' && document.documentElement.dataset.specimenHoverDelegated !== 'true') {
    document.documentElement.dataset.specimenHoverDelegated = 'true';

    const activateFromEvent = (event) => {
      const media = mediaFromEventTarget(event.target);
      if (!media || media.dataset.secondaryLoaded === 'true') return;
      loadCardSecondary(media);
    };

    // mouseover/focusin bubble — covers cards added later without rebinding.
    document.addEventListener('mouseover', activateFromEvent, { passive: true });
    document.addEventListener('focusin', activateFromEvent);
  }

  initSecondaryPrefetch(root);
}

if (typeof document !== 'undefined') {
  const boot = () => bindCardHover();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    bindCardHover(event.target);
  });

  /** @type {Window & { __bindSpecimenCardHover?: typeof bindCardHover }} */
  (window).__bindSpecimenCardHover = bindCardHover;
}
